import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2, TrendingDown, Clock, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SUGGESTED = [
  "Which department had the most early exits this quarter?",
  "Show me all high-risk employees in Telesales.",
  "What is the top reason for attrition and what should we do about it?",
  "Which branch is falling behind on documentation completion?",
];

export default function AIAssistant() {
  const { user } = useAuth();
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [risks, setRisks] = useState([]);
  const [risksLoading, setRisksLoading] = useState(false);
  const [ttf, setTtf] = useState([]);

  useEffect(() => {
    api.get("/ai/time-to-fill-forecast").then((r) => setTtf(r.data.forecast || [])).catch(() => {});
  }, []);

  const ask = async (q) => {
    const text = (q || question).trim();
    if (!text) return;
    setAsking(true);
    setHistory((h) => [...h, { role: "user", text }]);
    try {
      const { data } = await api.post("/ai/query", { question: text });
      setHistory((h) => [...h, { role: "ai", text: data.answer }]);
      setQuestion("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI service error");
    } finally {
      setAsking(false);
    }
  };

  const runSummary = async () => {
    setSummaryLoading(true);
    try {
      const { data } = await api.post("/ai/monthly-summary");
      setSummary(data.summary);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI service error");
    } finally {
      setSummaryLoading(false);
    }
  };

  const runRisks = async () => {
    setRisksLoading(true);
    try {
      const { data } = await api.post("/ai/attrition-risk");
      setRisks(data.predicted_risks || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI service error");
    } finally {
      setRisksLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-page">
      <div>
        <div className="label-eyebrow">HR Intelligence</div>
        <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1 flex items-center gap-3">
          <Sparkles className="h-6 w-6 text-[var(--yc-gold)]" /> Claude Sonnet 4.5
        </h1>
        <p className="text-slate-600 text-sm mt-1">
          Ask questions of your live HR data. Predict risk. Generate one-click monthly summaries for the Founder.
        </p>
      </div>

      {/* Query bar */}
      <div className="bg-white border-2 border-[var(--yc-gold)]/40 rounded-lg p-6">
        <div className="label-eyebrow mb-2">Natural language query</div>
        <form onSubmit={(e) => { e.preventDefault(); ask(question); }} className="flex gap-2">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g., Which branch has the highest attrition and why?"
            className="h-11 bg-white"
            data-testid="ai-query-input"
          />
          <Button
            type="submit"
            disabled={asking || !question.trim()}
            className="h-11 rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)] px-5"
            data-testid="ai-ask-button"
          >
            {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Ask</>}
          </Button>
        </form>
        <div className="mt-3 flex flex-wrap gap-2">
          {SUGGESTED.map((s) => (
            <button
              key={s}
              onClick={() => ask(s)}
              className="text-xs text-slate-600 hover:text-[var(--yc-navy)] border border-slate-200 hover:border-[var(--yc-gold)] rounded-full px-3 py-1.5 transition-colors bg-slate-50"
              data-testid={`ai-suggest-${s.slice(0,10)}`}
            >
              {s}
            </button>
          ))}
        </div>

        {history.length > 0 && (
          <div className="mt-6 space-y-4 max-h-96 overflow-y-auto">
            {history.map((h, i) => (
              <div key={i} className={`flex ${h.role === "user" ? "justify-end" : "justify-start"}`}>
                <div data-testid={`ai-message-${h.role}-${i}`} className={`max-w-2xl px-4 py-3 rounded-lg text-sm leading-relaxed ${
                  h.role === "user"
                    ? "bg-[var(--yc-navy)] text-white"
                    : "bg-[var(--yc-gold)]/10 border border-[var(--yc-gold)]/30 text-[var(--yc-navy)] whitespace-pre-wrap"
                }`}>
                  {h.text}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Predicted attrition risk */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">Predictive</div>
              <div className="font-heading font-semibold text-[var(--yc-navy)] flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-[var(--yc-rose)]" /> Attrition risk (30/60/90 days)
              </div>
            </div>
            <Button variant="outline" onClick={runRisks} disabled={risksLoading} data-testid="predict-risk-button">
              {risksLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Predict now"}
            </Button>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {risks.length === 0 && <div className="text-sm text-slate-500">          Click &ldquo;Predict now&rdquo; to see forecasted risks.</div>}
            {risks.map((r) => (
              <div key={r.employee_id} className="border border-slate-100 rounded-md p-3 hover:border-slate-200 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-[var(--yc-navy)] text-sm">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.department} · {r.branch}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
                    r.risk_score >= 6 ? "rag-red" : r.risk_score >= 4 ? "rag-amber" : "rag-green"
                  }`}>
                    ~{r.window}
                  </span>
                </div>
                <div className="text-xs text-slate-600 mt-2">{r.reasons.join(" · ")}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Time-to-Fill Forecast */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="mb-4">
            <div className="label-eyebrow">Forecast</div>
            <div className="font-heading font-semibold text-[var(--yc-navy)] flex items-center gap-2">
              <Clock className="h-4 w-4 text-[var(--yc-emerald)]" /> Time-to-Fill by Department
            </div>
          </div>
          <div className="space-y-2">
            {ttf.length === 0 && <div className="text-sm text-slate-500">No historical data yet — join more candidates to enable forecasting.</div>}
            {ttf.map((f) => (
              <div key={f.department} className="flex items-center justify-between border-b border-slate-100 py-2">
                <div>
                  <div className="text-sm font-medium text-[var(--yc-navy)]">{f.department}</div>
                  <div className="text-xs text-slate-500">n = {f.sample}</div>
                </div>
                <div className="font-heading font-bold text-xl text-[var(--yc-navy)]">
                  {f.avg_days}<span className="text-xs text-slate-500 ml-1">days</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Monthly summary */}
      {(user?.role === "super_admin" || user?.role === "hr_manager") && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">One-click briefing</div>
              <div className="font-heading font-semibold text-[var(--yc-navy)] flex items-center gap-2">
                <FileText className="h-4 w-4 text-[var(--yc-gold)]" /> Monthly HR Summary — for the Founder
              </div>
            </div>
            <Button onClick={runSummary} disabled={summaryLoading} className="rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="generate-summary-button">
              {summaryLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate summary"}
            </Button>
          </div>
          {summary ? (
            <div className="bg-slate-50 border border-slate-100 rounded-md p-4 text-sm leading-relaxed whitespace-pre-wrap text-[var(--yc-navy)]">
              {summary}
            </div>
          ) : (
            <div className="text-sm text-slate-500">Wins, risks and next actions — drafted from the same data every module tracks.</div>
          )}
        </div>
      )}
    </div>
  );
}
